import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, PhoneCall, CheckCircle2 } from "lucide-react";

const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const INTERESTS = [
  { id: "scale", label: "Scale plan" },
  { id: "enterprise", label: "Enterprise plan" },
  { id: "sovereign", label: "Sovereign / on-prem" },
  { id: "sso", label: "SSO enablement" },
  { id: "security", label: "Security / vendor review" },
  { id: "general", label: "General inquiry" },
];
const TIMES = [
  "Weekday mornings (UTC)",
  "Weekday afternoons (UTC)",
  "Weekday evenings (UTC)",
  "Weekends (UTC)",
  "Flexible — any time",
];

export default function BookCallDialog({ open, onClose, defaultInterest = "general" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    company_size: "51-200",
    preferred_time: "Flexible — any time",
    interest: defaultInterest,
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // request id on success

  // Sync interest + clear prior success state whenever dialog is (re)opened
  // with a potentially different defaultInterest.
  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, interest: defaultInterest }));
      setDone(null);
    }
  }, [open, defaultInterest]);

  if (!open) return null;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/sales/request-call", form);
      setDone(data.id);
      toast.success("Request received. A DARA rep will call you within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed — please try again");
    }
    setBusy(false);
  };

  const close = () => {
    setDone(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="book-call-dialog">
      <div className="panel max-w-xl w-full max-h-[90vh] overflow-auto">
        <header className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between sticky top-0 bg-black">
          <div className="flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-[#FFB000]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">// BOOK A CALL</span>
          </div>
          <button onClick={close} className="text-zinc-500 hover:text-white" data-testid="close-book-call">
            <X className="w-4 h-4" />
          </button>
        </header>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#00E676] mx-auto mb-4" />
            <h2 className="font-heading font-black text-2xl uppercase mb-2">Request received.</h2>
            <p className="text-sm text-zinc-400 mb-6 max-w-sm mx-auto">
              Your reference ID is <span className="font-mono text-[#FFB000]">{done.slice(0, 8)}</span>.
              A member of the DARA enterprise team will reach out to <span className="text-white">{form.phone}</span> or <span className="text-white">{form.email}</span> within 24 business hours.
            </p>
            <button
              onClick={close}
              data-testid="book-call-close-success"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-2.5 hover:bg-[#E59E00]"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div>
              <h2 className="font-heading font-black text-2xl uppercase tracking-tighter">Speak with a DARA rep</h2>
              <p className="text-sm text-zinc-500 mt-1">Tell us who you are and when to call. Response within 24 hours.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input data-testid="call-name" required value={form.name} onChange={update("name")} className={fieldCls} />
              </Field>
              <Field label="Company" required>
                <input data-testid="call-company" required value={form.company} onChange={update("company")} className={fieldCls} />
              </Field>
              <Field label="Work email" required>
                <input data-testid="call-email" type="email" required value={form.email} onChange={update("email")} className={fieldCls} />
              </Field>
              <Field label="Phone (include country code)" required>
                <input
                  data-testid="call-phone"
                  type="tel" required value={form.phone} onChange={update("phone")}
                  placeholder="+1 555 123 4567"
                  className={fieldCls}
                />
              </Field>
              <Field label="Company size">
                <select data-testid="call-size" value={form.company_size} onChange={update("company_size")} className={fieldCls}>
                  {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Interested in">
                <select data-testid="call-interest" value={form.interest} onChange={update("interest")} className={fieldCls}>
                  {INTERESTS.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
                </select>
              </Field>
              <Field label="Preferred time" full>
                <select data-testid="call-time" value={form.preferred_time} onChange={update("preferred_time")} className={fieldCls}>
                  {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Message (optional)" full>
                <textarea
                  data-testid="call-message"
                  rows={3}
                  value={form.message} onChange={update("message")}
                  placeholder="Anything specific you'd like to discuss?"
                  className={fieldCls + " resize-none"}
                />
              </Field>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-900">
              <button type="button" onClick={close} className="border border-zinc-700 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900">
                CANCEL
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="call-submit-btn"
                className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-2 hover:bg-[#E59E00] disabled:opacity-50 flex items-center gap-2"
              >
                <PhoneCall className="w-3.5 h-3.5" /> {busy ? "SENDING…" : "REQUEST CALL"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const fieldCls = "mt-2 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";

function Field({ label, children, full, required }) {
  return (
    <label className={full ? "col-span-2 block" : "block"}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        {label}{required && <span className="text-[#FFB000]"> *</span>}
      </span>
      {children}
    </label>
  );
}
